import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');`;

/* ─── DATA ─────────────────────────────────────────────────────────────────── */
const JOBS = [
  {
    id: 2,
    title: "SDE Intern — UI/UX",
    type: "Internship",
    dept: "Design",
    client: "Client Project",
    openings: 1,
    accent: "#6366F1",
    glyph: "design",
    tags: ["Figma", "User Research", "Prototyping", "Design Systems"],
    compensation: "₹15,000 – ₹17,000 / month",
    compensationShort: "₹15k – 17k / mo",
    duration: "3–6 Months",
    rounds: 5,
    location: "Remote",
    workMode: "Remote",
    description: "This role is for designers who think in systems, not screens. You will design end-to-end user experiences for a live client product — from user research and wireframes to high-fidelity prototypes and developer handoffs.",
    responsibilities: [
      "Conduct user research, interviews, and competitive analysis",
      "Create wireframes, user flows, and interactive prototypes in Figma",
      "Build and maintain a consistent component-based design system",
      "Work closely with frontend engineers to ensure accurate implementation",
      "Iterate rapidly based on feedback and usability testing",
    ],
    requirements: [
      "Proficiency in Figma with a strong portfolio of UI/UX work",
      "Understanding of UX principles, accessibility, and design systems",
      "Ability to articulate and defend design decisions clearly",
      "Experience working on developer handoffs",
      "Keen eye for typography, spacing, and visual hierarchy",
    ],
  },
  {
    id: 6,
    title: "Java Tutor",
    type: "Part-Time",
    dept: "Education",
    client: "GrapplTech",
    openings: null,
    accent: "#DC2626",
    glyph: "teach",
    tags: ["Java", "OOP", "DSA", "Mentorship", "Live Sessions"],
    compensation: "Paid on an hourly basis, based on experience",
    compensationShort: "Hourly Pay",
    duration: "Ongoing",
    rounds: 5,
    location: "Remote",
    workMode: "Remote",
    description: "GrapplTech is looking for an experienced Java instructor to teach structured, engaging live sessions to our bootcamp cohorts. You will deliver curriculum, mentor students, and ensure every learner leaves with production-level Java skills — not just theory.",
    responsibilities: [
      "Deliver live Java sessions covering OOP, Collections, DSA, and advanced topics",
      "Design assignments, quizzes, and hands-on mini-projects for each module",
      "Provide 1-on-1 doubt-clearing and personalised mentorship to learners",
      "Review student code and give structured, actionable written feedback",
      "Continuously improve curriculum based on learner performance data",
    ],
    requirements: [
      "2+ years of Java development or teaching experience",
      "Deep understanding of OOP, Collections, Streams, and DSA in Java",
      "Ability to explain complex concepts clearly and engagingly",
      "Strong communication skills in English and / or Telugu",
      "Genuine passion for education and measurable learner outcomes",
    ],
  },
  {
    id: 9,
    title: "Java & Python Tutor — Madanapalle",
    type: "Part-Time",
    dept: "Education",
    client: "GrapplTech",
    openings: null,
    accent: "#EA580C",
    glyph: "teach",
    tags: ["Java", "Python", "On-site", "Madanapalle", "Mentorship"],
    compensation: "Paid on an hourly basis · Accommodation provided",
    compensationShort: "Hourly + Stay",
    duration: "Ongoing",
    rounds: 5,
    location: "Madanapalle, Andhra Pradesh",
    workMode: "On-site",
    special: "On-site Role",
    description: "GrapplTech is hiring an on-site instructor based in Madanapalle to teach Java and Python to our learners in person. You will run structured classroom sessions, guide hands-on practice, and give learners the same rigour as our live online cohorts — with the added advantage of in-person mentorship.",
    responsibilities: [
      "Conduct in-person Java and Python classes for learners in Madanapalle",
      "Plan lesson structures, exercises, and assessments for each batch",
      "Provide hands-on, one-on-one support during lab and practice sessions",
      "Track learner progress and share regular performance updates",
      "Coordinate with the curriculum team to keep content aligned with our other cohorts",
    ],
    requirements: [
      "Strong working knowledge of both Java and Python fundamentals",
      "Prior teaching, training, or mentoring experience preferred",
      "Comfortable conducting in-person classroom sessions in Madanapalle",
      "Clear communicator in English and / or Telugu",
      "Reliable, organised, and consistent with session delivery",
    ],
  },
  {
    id: 7,
    title: "Business Development Associate",
    type: "Full-Time",
    dept: "Business",
    client: "GrapplTech",
    openings: null,
    accent: "#0891B2",
    glyph: "sales",
    tags: ["Sales", "Outreach", "EdTech", "CRM", "Growth"],
    compensation: "As per experience",
    compensationShort: "Salary + Incentives",
    duration: "Ongoing",
    rounds: 5,
    location: "Hybrid — Office + Remote",
    workMode: "Hybrid",
    description: "As a BDA at GrapplTech, you will be the bridge between our programs and the learners who need them. You will identify prospects, have meaningful conversations, and guide students and professionals toward the right bootcamp or service — building long-term relationships that fuel growth.",
    responsibilities: [
      "Identify and reach out to potential learners via calls, DMs, and email",
      "Conduct consultative conversations to understand learner goals and constraints",
      "Present GrapplTech's bootcamps and services with clarity and confidence",
      "Follow up consistently and manage leads through the CRM",
      "Collaborate with the marketing team on outreach campaigns",
    ],
    requirements: [
      "Excellent communication and interpersonal skills",
      "Ability to handle objections and drive goal-oriented conversations",
      "Self-motivated with a strong sense of ownership over outcomes",
      "Familiarity with CRM tools or willingness to learn quickly",
      "Prior sales or EdTech experience is a strong advantage",
    ],
  },
  {
    id: 8,
    title: "Campus Ambassador",
    type: "Part-Time",
    dept: "Community",
    client: "GrapplTech",
    openings: 8,
    accent: "#028EC4",
    glyph: "community",
    tags: ["Community", "Campus Outreach", "Batch 2027", "Batch 2028"],
    compensation: "Paid per activation, based on outcomes",
    compensationShort: "Performance-Based",
    duration: "Ongoing",
    rounds: 5,
    location: "Remote + Campus",
    workMode: "Hybrid",
    special: "Batch 2027 & 2028 Only",
    description: "Be the face of GrapplTech at your institution. You will represent GrapplTech among your peers, raise awareness about our bootcamps, and help students make informed career decisions. Strictly open to students graduating in 2027 or 2028.",
    responsibilities: [
      "Promote GrapplTech bootcamps and programs within your college",
      "Organise and host awareness sessions, workshops, or demo events on campus",
      "Onboard fellow students to GrapplTech programs and track referrals",
      "Act as a feedback bridge between the learner community and GrapplTech",
      "Build and grow a GrapplTech community at your institution",
    ],
    requirements: [
      "Currently enrolled — graduating batch of 2027 or 2028 strictly",
      "Strong campus presence, leadership skills, and peer credibility",
      "Genuine interest in tech education and career development",
      "Active on social media and comfortable with structured outreach",
      "Self-driven, reliable, and consistent with follow-through",
    ],
  },
];

const WHY_POINTS = [
  { num: "01", title: "Production Work from Day One",    desc: "No dummy tasks. Every team member ships real work on live systems.", area: "a", color: "#35B5FF", big: true },
  { num: "02", title: "Transparent Compensation",         desc: "Every role has clearly defined pay — no ambiguity, no surprises.", area: "b", color: "#FCA701" },
  { num: "03", title: "Flexible Work Models",             desc: "Remote, hybrid, or on-site — matched to what the role actually needs.", area: "c", color: "#82B535" },
  { num: "04", title: "Complete Work Setup Support",      desc: "For roles that need it, we equip you with the gear you need — from a laptop to a stable internet connection — so nothing gets in the way of doing your best work.", area: "d", color: "#C864ED", wide: true },
  { num: "05", title: "Steep Learning Curve",             desc: "You will learn more in three months here than in most year-long programs.", area: "e", color: "#FFC635" },
  { num: "06", title: "Mentorship from Practitioners",    desc: "Guided by professionals who build for a living — not just managers.", area: "g", color: "#E61B40", wide: true },
  { num: "07", title: "Clear Growth Trajectory",          desc: "Impact drives advancement here — not tenure or seniority.", area: "f", color: "#FCA701" },
];

const HIRING_STEPS = [
  { step: "01", title: "Application Review",   desc: "Our team reviews your resume and portfolio against the role's requirements." },
  { step: "02", title: "Screening Call",       desc: "A short conversation to understand your background, motivation, and fit." },
  { step: "03", title: "Skill Assessment",     desc: "Role-specific rounds — a task, technical interview, or teaching demo as applicable." },
  { step: "04", title: "Final Discussion",     desc: "A closing conversation on expectations, compensation, and start date." },
];

/* ─── SVG ICONS ────────────────────────────────────────────────────────────── */
const IconMail    = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const IconArrow   = ({ size = 13 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const IconClose   = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconCheck   = ({ size = 11 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconPin     = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconClock   = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconUsers   = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconMoney   = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9h.01M15 15h.01M15 9l-6 6"/></svg>;
const IconLaptop  = ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M2 20h20"/></svg>;
const IconBuilding= ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="1"/><path d="M9 22v-4h6v4M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1"/></svg>;
const IconShuffle = ({ size = 22 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>;

/* Role glyphs — one per job family, used in the card badge */
const IconDesign = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>;
const IconTeach   = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>;
const IconSales   = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconCommunity = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>;

const ROLE_GLYPHS = { design: IconDesign, teach: IconTeach, sales: IconSales, community: IconCommunity };

const WORK_MODE_COLORS = {
  Remote:  { bg: "rgba(5,150,105,0.1)",  border: "rgba(5,150,105,0.24)",  text: "#059669" },
  "On-site": { bg: "rgba(234,88,12,0.1)", border: "rgba(234,88,12,0.24)", text: "#C2410C" },
  Hybrid:  { bg: "rgba(2,142,196,0.1)",  border: "rgba(2,142,196,0.24)",  text: "#028EC4" },
};

/* ─── JD MODAL ─────────────────────────────────────────────────────────────── */
function JDModal({ job, onClose, isDark }) {
  if (!job) return null;

  const bg      = isDark ? "#0F0F17"                : "#ffffff";
  const overlay = isDark ? "rgba(0,0,0,0.75)"       : "rgba(0,0,0,0.45)";
  const border  = isDark ? "rgba(255,255,255,0.1)"  : "#E2E8F0";
  const text    = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted   = isDark ? "rgba(255,255,255,0.46)" : "#64748B";
  const divClr  = isDark ? "rgba(255,255,255,0.07)" : "#F1F5F9";
  const tagBg   = isDark ? "rgba(255,255,255,0.07)" : "#F1F5F9";
  const tagClr  = isDark ? "rgba(255,255,255,0.5)"  : "#475569";
  const metaBg  = isDark ? "rgba(255,255,255,0.05)" : "#F8FAFC";
  const wm      = WORK_MODE_COLORS[job.workMode] || WORK_MODE_COLORS.Remote;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: overlay, backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 16 }}
        transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={e => e.stopPropagation()}
        style={{
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 20,
          width: "100%", maxWidth: 640,
          maxHeight: "88vh",
          overflowY: "auto",
          position: "relative",
          boxShadow: isDark
            ? "0 0 0 1px rgba(255,255,255,0.05), 0 40px 80px rgba(0,0,0,0.6)"
            : "0 4px 6px rgba(0,0,0,0.05), 0 40px 80px rgba(0,0,0,0.12)",
        }}
      >
        {/* Accent top border */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${job.accent}, ${job.accent}55)`, borderRadius: "20px 20px 0 0" }} />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 18, right: 18,
            width: 32, height: 32, borderRadius: "50%",
            background: isDark ? "rgba(255,255,255,0.07)" : "#F1F5F9",
            border: `1px solid ${border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: muted,
          }}
        >
          <IconClose />
        </button>

        {/* Modal header */}
        <div style={{ padding: "28px 32px 24px" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: job.accent + "14", color: job.accent,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", padding: "3px 10px",
              borderRadius: 5, border: `1px solid ${job.accent}28`,
            }}>
              {job.type} &nbsp;·&nbsp; {job.dept}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: wm.bg, color: wm.text,
              fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
              textTransform: "uppercase", padding: "3px 10px",
              borderRadius: 5, border: `1px solid ${wm.border}`,
            }}>
              {job.workMode}
            </span>
          </div>

          <h2 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: "clamp(1.3rem, 4vw, 1.75rem)",
            fontWeight: 800, color: text,
            margin: "0 0 20px", lineHeight: 1.2,
          }}>
            {job.title}
          </h2>

          {/* Meta chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {[
              [<IconClock />, job.duration],
              [<IconPin />,   job.location],
              [<IconMoney />, job.compensation],
              [<IconUsers />, job.client],
              ...(job.openings ? [[<IconUsers />, `${job.openings} Opening${job.openings > 1 ? "s" : ""}`]] : []),
            ].map(([icon, val], i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, color: muted, background: metaBg, border: `1px solid ${divClr}`, padding: "5px 12px", borderRadius: 7 }}>
                <span style={{ color: muted, display: "flex", alignItems: "center" }}>{icon}</span>
                {val}
              </span>
            ))}
          </div>

          {/* Tags */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {job.tags.map(t => (
              <span key={t} style={{ fontSize: 11.5, fontWeight: 500, padding: "3px 10px", borderRadius: 5, background: tagBg, border: `1px solid ${divClr}`, color: tagClr }}>{t}</span>
            ))}
          </div>
        </div>

        <div style={{ height: 1, background: divClr, margin: "0 32px" }} />

        {/* About the role */}
        <div style={{ padding: "24px 32px" }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: job.accent, margin: "0 0 10px" }}>About the Role</p>
          <p style={{ fontSize: 14.5, color: muted, lineHeight: 1.82, margin: 0 }}>{job.description}</p>
        </div>

        <div style={{ height: 1, background: divClr, margin: "0 32px" }} />

        {/* Responsibilities */}
        <div style={{ padding: "24px 32px 0" }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: job.accent, margin: "0 0 14px" }}>Responsibilities</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {job.responsibilities.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                <span style={{ width: 18, height: 18, borderRadius: "50%", background: job.accent + "18", color: job.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <IconCheck />
                </span>
                <span style={{ fontSize: 14, color: muted, lineHeight: 1.65 }}>{r}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ height: 1, background: divClr, margin: "24px 32px" }} />

        {/* Requirements */}
        <div style={{ padding: "0 32px" }}>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: muted, margin: "0 0 14px" }}>Requirements</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {job.requirements.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                <span style={{ width: 18, height: 18, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9", border: `1px solid ${divClr}`, color: muted, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                  <IconCheck />
                </span>
                <span style={{ fontSize: 14, color: muted, lineHeight: 1.65 }}>{r}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Apply footer */}
        <div style={{ margin: "28px 32px 32px", padding: "20px 24px", background: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC", border: `1px solid ${divClr}`, borderRadius: 12 }}>
          <p style={{ fontSize: 12, color: muted, margin: "0 0 3px" }}>Ready to apply?</p>
          <p style={{ fontSize: 14, fontWeight: 600, color: text, margin: "0 0 16px" }}>
            Send your resume and a brief introduction to{" "}
            <span style={{ color: job.accent }}>careers@grappl-tech.in</span>
          </p>
          <a
            href={`mailto:careers@grappl-tech.in?subject=Application: ${job.title}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: job.accent, color: "#fff",
              fontWeight: 700, fontSize: 13.5,
              padding: "11px 22px", borderRadius: 10,
              textDecoration: "none",
              transition: "filter 0.18s",
              fontFamily: "'DM Sans', sans-serif",
            }}
            onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
            onMouseLeave={e => e.currentTarget.style.filter = "none"}
          >
            <IconMail /> Apply via Email
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── JOB CARD — modern glyph-led design ────────────────────────────────────── */
function JobCard({ job, onOpen, isDark, index }) {
  const [hovered, setHovered] = useState(false);
  const Glyph = ROLE_GLYPHS[job.glyph] || IconUsers;
  const wm = WORK_MODE_COLORS[job.workMode] || WORK_MODE_COLORS.Remote;

  const bg      = isDark
    ? "linear-gradient(160deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.015) 100%)"
    : "linear-gradient(160deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.92) 100%)";
  const border  = isDark ? "rgba(255,255,255,0.09)" : "rgba(15,23,42,0.08)";
  const text    = isDark ? "rgba(255,255,255,0.94)" : "#0F172A";
  const muted   = isDark ? "rgba(255,255,255,0.46)" : "#64748B";
  const divClr  = isDark ? "rgba(255,255,255,0.07)" : "#EEF2F6";
  const tagBg   = isDark ? "rgba(255,255,255,0.055)" : "rgba(15,23,42,0.035)";
  const tagClr  = isDark ? "rgba(255,255,255,0.5)"  : "#475569";
  const tagBord = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 20,
        overflow: "hidden",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      animate={{
        y: hovered ? -4 : 0,
        boxShadow: hovered
          ? (isDark
              ? `0 0 0 1px ${job.accent}3a, 0 20px 44px -12px ${job.accent}40, 0 1px 0 rgba(255,255,255,0.06) inset`
              : `0 0 0 1px ${job.accent}2e, 0 20px 40px -14px ${job.accent}38`)
          : (isDark
              ? "0 0 0 1px rgba(255,255,255,0.02), 0 1px 0 rgba(255,255,255,0.04) inset"
              : "0 1px 2px rgba(15,23,42,0.03)"),
      }}
    >
      {/* Ambient corner glow — the one signature flourish, quiet everywhere else */}
      <div style={{
        position: "absolute", top: -60, right: -60, width: 180, height: 180,
        borderRadius: "50%", background: job.accent,
        opacity: isDark ? 0.1 : 0.07, filter: "blur(40px)",
        pointerEvents: "none", transition: "opacity 0.25s",
      }} />

      <div style={{ padding: "24px 24px 22px", position: "relative" }}>

        {/* Row 1: glyph badge + type/openings */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
          <motion.div
            animate={{ scale: hovered ? 1.06 : 1, rotate: hovered ? -3 : 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{
              width: 46, height: 46, borderRadius: 13,
              background: `linear-gradient(135deg, ${job.accent}, ${job.accent}CC)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", flexShrink: 0,
              boxShadow: `0 6px 16px -4px ${job.accent}66`,
            }}
          >
            <Glyph />
          </motion.div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.11em",
              textTransform: "uppercase", color: muted,
            }}>
              {job.type}
            </span>
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", padding: "2px 8px", borderRadius: 5,
              background: wm.bg, color: wm.text,
              border: `1px solid ${wm.border}`,
            }}>
              {job.workMode}
            </span>
            {job.special && (
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em",
                textTransform: "uppercase", padding: "2px 8px", borderRadius: 5,
                background: "rgba(202,138,4,0.1)", color: "#B45309",
                border: "1px solid rgba(202,138,4,0.22)",
              }}>
                {job.special}
              </span>
            )}
          </div>
        </div>

        {/* Row 2: Title + dept */}
        <p style={{ fontSize: 11, fontWeight: 600, color: job.accent, margin: "0 0 4px", letterSpacing: "0.02em" }}>{job.dept}</p>
        <h3 style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: "clamp(1.05rem, 3vw, 1.22rem)",
          fontWeight: 800, color: text,
          margin: "0 0 16px", lineHeight: 1.28,
          wordBreak: "break-word", overflowWrap: "break-word",
        }}>
          {job.title}
        </h3>

        {/* Row 3: Meta — quiet inline text, no boxes */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 14px", marginBottom: 16, fontSize: 12.5, color: muted }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><IconClock size={12} />{job.duration}</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><IconPin size={12} />{job.location}</span>
          {job.openings && (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><IconUsers size={12} />{job.openings} Opening{job.openings > 1 ? "s" : ""}</span>
            </>
          )}
        </div>

        {/* Row 4: Tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 20 }}>
          {job.tags.slice(0, 4).map(t => (
            <span key={t} style={{
              fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 999,
              background: tagBg, border: `1px solid ${tagBord}`, color: tagClr,
              letterSpacing: "0.01em",
            }}>{t}</span>
          ))}
        </div>

        {/* Row 5: Footer */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, paddingTop: 16, borderTop: `1px solid ${divClr}` }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#059669" }}>
            <IconMoney size={11} />
            {job.compensationShort} · {job.rounds} Rounds
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a
              href={`mailto:careers@grappl-tech.in?subject=Application: ${job.title}`}
              onClick={e => e.stopPropagation()}
              title="Email your resume"
              style={{
                width: 32, height: 32, borderRadius: 9,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: `1px solid ${tagBord}`, background: tagBg, color: muted,
                textDecoration: "none", transition: "all 0.18s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = job.accent; e.currentTarget.style.borderColor = job.accent + "44"; }}
              onMouseLeave={e => { e.currentTarget.style.color = muted; e.currentTarget.style.borderColor = tagBord; }}
            >
              <IconMail size={13} />
            </a>
            <button
              onClick={() => onOpen(job)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 12.5, fontWeight: 700, color: "#fff",
                padding: "8px 15px", border: "none",
                borderRadius: 9, background: job.accent,
                cursor: "pointer", transition: "filter 0.18s, transform 0.18s",
                fontFamily: "'DM Sans', sans-serif",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
              onMouseLeave={e => e.currentTarget.style.filter = "none"}
            >
              View Role <IconArrow />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function Careers() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const [filter, setFilter] = useState("All");
  const [workModeFilter, setWorkModeFilter] = useState("All");
  const [activeJob, setActiveJob] = useState(null);

  const FILTERS = ["All", "Internship", "Full-Time", "Part-Time"];
  const WORK_MODE_FILTERS = ["All", "Remote", "Hybrid", "On-site"];

  const filtered = JOBS
    .filter(j => filter === "All" || j.type.startsWith(filter))
    .filter(j => workModeFilter === "All" || j.workMode === workModeFilter);

  const pageBg    = isDark ? "#080810"                : "#F0F4F8";
  const heroBord  = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  const heroGlow  = isDark ? "rgba(2,142,196,0.08)"   : "rgba(2,142,196,0.05)";
  const text      = isDark ? "rgba(255,255,255,0.93)" : "#0F172A";
  const muted     = isDark ? "rgba(255,255,255,0.44)" : "#64748B";
  const pillBg    = isDark ? "rgba(2,142,196,0.1)"    : "rgba(2,142,196,0.07)";
  const pillBord  = isDark ? "rgba(2,142,196,0.28)"   : "rgba(2,142,196,0.2)";
  const statBg    = isDark
    ? "rgba(255,255,255,0.04)"
    : "rgba(255,255,255,0.8)";
  const statBord  = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const fBg       = isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.85)";
  const fBord     = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.09)";
  const fText     = isDark ? "rgba(255,255,255,0.48)" : "#475569";
  const cardBg    = isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.85)";
  const cardBord  = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";

  const HOW_WE_WORK = [
    { icon: IconLaptop,   title: "Remote Roles",  color: "#059669", desc: "Several roles — including our internships and online tutoring positions — are fully remote. You work from wherever you are, with output as the only measure." },
    { icon: IconBuilding, title: "On-site Roles", color: "#C2410C", desc: "Some positions, like our Madanapalle tutoring role, require working from a physical location so learners get consistent, in-person support." },
    { icon: IconShuffle,  title: "Hybrid Roles",  color: "#028EC4", desc: "Roles like Business Development and Campus Ambassador combine remote work with periodic in-person time — at our office or on campus." },
  ];

  return (
    <>
      <style>{`
        ${FONT_LINK}
        *, *::before, *::after { box-sizing: border-box; }
        .cr-page { font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; }
        .cr-jobs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
        .cr-how-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .cr-hiring-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .cr-why-bento {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-auto-rows: minmax(150px, auto);
          gap: 14px;
          grid-template-areas:
            "a a b c"
            "a a d d"
            "e f g g";
        }
        @media (max-width: 980px) {
          .cr-why-bento {
            grid-template-columns: repeat(2, 1fr);
            grid-template-areas:
              "a a"
              "a a"
              "b c"
              "d d"
              "e f"
              "g g";
          }
          .cr-how-grid { grid-template-columns: 1fr; }
          .cr-hiring-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .cr-why-bento {
            grid-template-columns: 1fr;
            grid-auto-rows: auto;
            grid-template-areas: "a" "b" "c" "d" "e" "f" "g";
          }
        }
        @media (max-width: 400px) {
          .cr-jobs-grid { grid-template-columns: 1fr; }
          .cr-hiring-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* JD Modal — rendered outside normal flow */}
      <AnimatePresence>
        {activeJob && (
          <JDModal job={activeJob} onClose={() => setActiveJob(null)} isDark={isDark} />
        )}
      </AnimatePresence>

      <div className="cr-page" style={{ background: pageBg, minHeight: "100vh", padding: "0 0 120px" }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <div style={{
          textAlign: "center", padding: "88px 20px 64px",
          background: `radial-gradient(ellipse 65% 38% at 50% 0%, ${heroGlow} 0%, transparent 70%)`,
          borderBottom: `1px solid ${heroBord}`,
        }}>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              background: pillBg, border: `1px solid ${pillBord}`,
              color: "#028EC4", fontSize: 10, fontWeight: 700,
              letterSpacing: "0.16em", textTransform: "uppercase",
              padding: "5px 14px", borderRadius: 5, marginBottom: 24,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#028EC4" }} />
              Now Hiring
            </span>

            <h1 style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: "clamp(2.4rem, 6vw, 4.4rem)",
              fontWeight: 800, lineHeight: 1.06,
              margin: "0 0 16px", color: text, letterSpacing: "-0.015em",
            }}>
              Careers at{" "}
              <span style={{ background: "linear-gradient(135deg, #028EC4, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                GrapplTech
              </span>
            </h1>

            <p style={{ fontSize: "clamp(13.5px, 2vw, 16px)", color: muted, maxWidth: 500, margin: "0 auto 40px", lineHeight: 1.82 }}>
              We are building an execution-first tech ecosystem. Ship real products, grow fast, and work on things that create genuine impact.
            </p>

            {/* Stats */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8, marginBottom: 36 }}>
              {[[String(JOBS.length), "Open Positions"], ["Hybrid", "Work Model"], ["Remote", "& On-site"], ["5", "Interview Rounds"]].map(([v, l]) => (
                <div key={l} style={{ padding: "9px 16px", background: statBg, border: `1px solid ${statBord}`, borderRadius: 9, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
                  <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "1.1rem", fontWeight: 800, color: text, lineHeight: 1 }}>{v}</div>
                  <div style={{ fontSize: 10.5, color: muted, marginTop: 3, letterSpacing: "0.04em" }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {FILTERS.map(f => {
                const active = filter === f;
                return (
                  <button key={f} onClick={() => setFilter(f)} style={{
                    padding: "7px 16px", borderRadius: 7,
                    fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                    transition: "all 0.18s",
                    border: `1px solid ${active ? "#028EC4" : fBord}`,
                    background: active ? "#028EC4" : fBg,
                    color: active ? "#fff" : fText,
                    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    {f}
                  </button>
                );
              })}
            </div>

            {/* Work mode filters */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
              {WORK_MODE_FILTERS.map(f => {
                const active = workModeFilter === f;
                return (
                  <button key={f} onClick={() => setWorkModeFilter(f)} style={{
                    padding: "5px 13px", borderRadius: 999,
                    fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                    transition: "all 0.18s",
                    border: `1px solid ${active ? "#0F172A" : fBord}`,
                    background: active ? (isDark ? "rgba(255,255,255,0.12)" : "#0F172A") : "transparent",
                    color: active ? "#fff" : fText,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    {f}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>

        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 18px" }}>

          {/* ── LISTINGS HEADER ─────────────────────────────────────────────── */}
          <div style={{ marginTop: 52, marginBottom: 20, display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 3px" }}>Open Positions</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.75rem)", fontWeight: 800, color: text, margin: 0 }}>
                {filtered.length} Role{filtered.length !== 1 ? "s" : ""}
              </h2>
            </div>
            <p style={{ fontSize: 12.5, color: muted }}>
              <a href="mailto:careers@grappl-tech.in" style={{ color: "#028EC4", fontWeight: 600, textDecoration: "none" }}>careers@grappl-tech.in</a>
            </p>
          </div>

          {/* ── CARDS GRID ───────────────────────────────────────────────────── */}
          <div className="cr-jobs-grid">
            {filtered.map((job, i) => (
              <JobCard key={job.id} job={job} onOpen={setActiveJob} isDark={isDark} index={i} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 20px", color: muted, fontSize: 13.5 }}>
              No roles match these filters right now — check back soon or send an open application below.
            </div>
          )}

          {/* ── HOW WE WORK ──────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 72 }}>
            <div style={{ marginBottom: 22 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 5px" }}>How We Work</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.75rem)", fontWeight: 800, color: text, margin: 0 }}>
                Remote, Hybrid, and On-site — By Design
              </h2>
              <p style={{ fontSize: 13.5, color: muted, margin: "10px 0 0", maxWidth: 620, lineHeight: 1.75 }}>
                We don't force every role into the same work model. Each position is set up the way that serves the work — and the learners — best.
              </p>
            </div>

            <div className="cr-how-grid">
              {HOW_WE_WORK.map((h, i) => {
                const Icon = h.icon;
                return (
                  <motion.div
                    key={h.title}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.06 }}
                    style={{
                      background: cardBg, border: `1px solid ${cardBord}`,
                      borderRadius: 16, padding: "24px",
                      backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                    }}
                  >
                    <div style={{
                      width: 42, height: 42, borderRadius: 11,
                      background: h.color + "16", color: h.color,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: 14,
                    }}>
                      <Icon />
                    </div>
                    <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "1.02rem", fontWeight: 800, color: text, margin: "0 0 8px" }}>{h.title}</h3>
                    <p style={{ fontSize: 13, color: muted, margin: 0, lineHeight: 1.7 }}>{h.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* ── WHY SECTION — colorful bento grid ───────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 72 }}>
            <div style={{ marginBottom: 22 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 5px" }}>Life at GrapplTech</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.75rem)", fontWeight: 800, color: text, margin: 0 }}>
                Why Work With Us
              </h2>
            </div>

            <div className="cr-why-bento">
              {WHY_POINTS.map((p, i) => (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                  whileHover={{ y: -3 }}
                  style={{
                    gridArea: p.area,
                    background: p.color,
                    borderRadius: 22,
                    padding: p.big ? "30px 30px 28px" : "24px 24px 22px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative",
                    overflow: "hidden",
                    minHeight: p.big ? 260 : 150,
                    boxShadow: `0 14px 32px -14px ${p.color}99`,
                  }}
                >
                  {/* quiet decorative glow — one recurring motif across every tile */}
                  <div style={{ position: "absolute", top: -46, right: -46, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.16)", pointerEvents: "none" }} />
                  <div style={{ position: "absolute", bottom: -60, left: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(0,0,0,0.06)", pointerEvents: "none" }} />

                  <span style={{
                    position: "relative", zIndex: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: p.big ? 36 : 30, height: p.big ? 36 : 30, borderRadius: 9,
                    background: "rgba(255,255,255,0.24)", color: "#fff",
                    fontSize: p.big ? 12 : 10.5, fontWeight: 800, letterSpacing: "0.04em",
                  }}>
                    {p.num}
                  </span>

                  <div style={{ position: "relative", zIndex: 1 }}>
                    <p style={{
                      fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800,
                      fontSize: p.big ? "clamp(1.3rem, 2.6vw, 1.6rem)" : "clamp(1rem, 2.2vw, 1.14rem)",
                      color: "#fff", margin: p.big ? "0 0 10px" : "0 0 6px", lineHeight: 1.22,
                    }}>
                      {p.title}
                    </p>
                    <p style={{
                      fontSize: p.big ? 14 : 12.5, color: "rgba(255,255,255,0.92)",
                      margin: 0, lineHeight: 1.66, maxWidth: p.big ? 340 : p.wide ? 420 : "none",
                    }}>
                      {p.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── HIRING PROCESS ──────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ marginTop: 72 }}>
            <div style={{ marginBottom: 22 }}>
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 5px" }}>Our Hiring Process</p>
              <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.75rem)", fontWeight: 800, color: text, margin: 0 }}>
                What to Expect
              </h2>
            </div>

            <div className="cr-hiring-grid">
              {HIRING_STEPS.map((s, i) => (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  style={{
                    background: cardBg, border: `1px solid ${cardBord}`,
                    borderRadius: 16, padding: "22px 20px",
                    backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                  }}
                >
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 30, height: 30, borderRadius: 9,
                    background: "rgba(2,142,196,0.1)", color: "#028EC4",
                    fontSize: 11, fontWeight: 800, marginBottom: 14,
                  }}>
                    {s.step}
                  </span>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "0.98rem", fontWeight: 800, color: text, margin: "0 0 6px" }}>{s.title}</h3>
                  <p style={{ fontSize: 12.5, color: muted, margin: 0, lineHeight: 1.65 }}>{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── OPEN APPLICATION ─────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }} style={{ marginTop: 56 }}>
            <div style={{
              border: `1px solid ${cardBord}`,
              borderRadius: 16, padding: "36px 40px",
              display: "flex", alignItems: "center",
              justifyContent: "space-between", flexWrap: "wrap", gap: 24,
              background: cardBg,
              backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
            }}>
              <div style={{ maxWidth: 460 }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 7px" }}>Open Application</p>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.2rem, 3vw, 1.55rem)", fontWeight: 800, color: text, margin: "0 0 8px" }}>
                  Don't see the right role?
                </h2>
                <p style={{ fontSize: 13.5, color: muted, margin: 0, lineHeight: 1.75 }}>
                  New roles open up as our products and programs expand. Send your resume and a brief introduction — we will reach out when the right opportunity comes up.
                </p>
              </div>
              <div>
                <a
                  href="mailto:careers@grappl-tech.in?subject=Open Application — GrapplTech"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    background: "#028EC4", color: "#fff",
                    fontWeight: 700, fontSize: 13.5,
                    padding: "11px 22px", borderRadius: 10,
                    textDecoration: "none",
                    boxShadow: "0 4px 14px rgba(2,142,196,0.28)",
                    transition: "filter 0.18s", whiteSpace: "nowrap",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                  onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                  onMouseLeave={e => e.currentTarget.style.filter = "none"}
                >
                  <IconMail /> Send Open Application <IconArrow />
                </a>
                <p style={{ fontSize: 11.5, color: muted, marginTop: 8, textAlign: "center" }}>careers@grappl-tech.in</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}